import React from "react";
import { FormHelperText } from "@mui/material";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DeficiencyResolutionPayload } from "@services/deficiencies/types";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  MAX_RESOLVE_DEFICIENCY_EXPLANATION_LENGTH,
  resolveDeficiencySchema,
  type ResolveDeficiencyFormData,
} from "./resolveDeficiencySchema";
import {
  RESOLUTION_PROOF_UPLOAD_KEY,
  useResolveDeficiencyProofUpload,
} from "./useResolveDeficiencyProofUpload";

interface ResolveDeficiencyDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: "resolveDrawer") => void;
  deficiencyId?: string;
  onSubmit?: (payload: DeficiencyResolutionPayload & { deficiencyId?: string }) => void;
  isSubmitting?: boolean;
}

const DRAWER_NAME = "resolveDrawer";
const MAX_RESOLUTION_PROOF_FILES = 10;
const RESOLUTION_PROOF_ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".pdf"];

const ResolveDeficiencyDrawer: React.FC<ResolveDeficiencyDrawerProps> = ({
  isOpen,
  onClose,
  deficiencyId,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const [fileError, setFileError] = React.useState<string | undefined>(undefined);

  const schema = React.useMemo(() => resolveDeficiencySchema(t), [t]);
  const form = useForm<ResolveDeficiencyFormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      explanation: "",
    },
  });

  const { handleSubmit, reset } = form;

  const fileProfiles = useFileUploadProfiles();
  const proofProfile = fileProfiles.sideDrawerApprovalFile;
  const {
    uploadEntries: proofFiles,
    setUploadEntries: setProofFiles,
    handleUpload,
    resetUpload,
    uploadedProofUrls,
    isUploading,
  } = useResolveDeficiencyProofUpload();

  const resetForm = React.useCallback(() => {
    reset({ explanation: "" });
    setFileError(undefined);
    resetUpload();
  }, [reset, resetUpload]);

  const prevOpenRef = React.useRef(isOpen);
  React.useEffect(() => {
    if (prevOpenRef.current && !isOpen) {
      resetForm();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, resetForm]);

  const handleClose = () => {
    onClose(DRAWER_NAME);
    resetForm();
  };

  const handleCancel = () => {
    onClose(DRAWER_NAME);
    resetForm();
  };

  const handleRemoveProof = (index: number) => {
    setProofFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const validateProofFiles = React.useCallback(() => {
    if (uploadedProofUrls.length === 0) {
      setFileError(
        t("form-validation.mandatory", {
          label: t("deficiencies.resolve-drawer.fields.resolution-proof"),
        }),
      );
      return null;
    }

    setFileError(undefined);
    return uploadedProofUrls;
  }, [t, uploadedProofUrls]);

  const handleInvalidForm = React.useCallback(() => {
    validateProofFiles();
  }, [validateProofFiles]);

  const handleSubmitForm = (data: ResolveDeficiencyFormData) => {
    const selectedFiles = validateProofFiles();

    if (!selectedFiles) {
      return;
    }

    onSubmit?.({
      deficiencyId,
      explanation: data.explanation.trim(),
      documents: selectedFiles.map((documentUrl, index) => ({
        documentUrl,
        documentName: proofFiles[index]?.name,
      })),
    });
  };

  if (!isOpen) return null;

  return (
    <div data-testid="resolve-deficiency-drawer-form" className="w-[400px] max-w-[100vw]">
      <SideDrawer
        isOpen={isOpen}
        title={t("deficiencies.resolve-drawer.title")}
        description={
          <span className="block max-w-[352px] whitespace-normal break-words">
            {t("deficiencies.resolve-drawer.description")}
          </span>
        }
        onClose={handleClose}
        onCancel={handleCancel}
        submitLabel={t("common.submit")}
        cancelLabel={t("common.cancel")}
        onSubmit={handleSubmit(handleSubmitForm, handleInvalidForm)}
        isSubmitting={Boolean(isSubmitting || isUploading)}
      >
        <div className="flex-1 w-full py-2">
          <div className="flex flex-col gap-2 px-6">
            <FormProvider {...form}>
              <FormItem<ResolveDeficiencyFormData, "explanation">
                required
                name="explanation"
                type="textarea"
                label={t("deficiencies.resolve-drawer.fields.explanation")}
                validationLabel={t("deficiencies.resolve-drawer.fields.explanation")}
                testId="resolve-deficiency-explanation"
                maxChars={MAX_RESOLVE_DEFICIENCY_EXPLANATION_LENGTH}
                showCharCounter
                minRows={4}
              />
            </FormProvider>

            <DocumentUpload
              label={t("deficiencies.resolve-drawer.fields.resolution-proof")}
              testId="resolve-deficiency-proof-upload"
              name={RESOLUTION_PROOF_UPLOAD_KEY}
              maxFiles={MAX_RESOLUTION_PROOF_FILES}
              allowedExtensions={RESOLUTION_PROOF_ALLOWED_EXTENSIONS}
              onUpload={(files) => {
                setFileError(undefined);
                handleUpload(files);
              }}
              conditions={proofProfile}
              files={proofFiles}
              setFiles={(next) => {
                setProofFiles(next);
                if (next.length > 0) {
                  setFileError(undefined);
                }
              }}
              uploadDescription={t("deficiencies.resolve-drawer.upload-description")}
              disabled={Boolean(isSubmitting || isUploading)}
            />

            {fileError && (
              <FormHelperText sx={{ color: "error.main", m: 0 }}>{fileError}</FormHelperText>
            )}

            {proofFiles.length > 0 && (
              <div className="flex flex-col gap-4">
                {proofFiles.map((file, index) => (
                  <SingleFile
                    key={`${file.name}-${file.lastModified ?? index}`}
                    testId={`resolve-deficiency-proof-${index}`}
                    file={file}
                    fieldName={RESOLUTION_PROOF_UPLOAD_KEY}
                    currentFileIndex={index}
                    setFiles={() => handleRemoveProof(index)}
                    isLoading={isUploading && file instanceof File}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default ResolveDeficiencyDrawer;
